import { logEvent } from "firebase/analytics";
import { analytics, getAnalyticsWhenReady } from "../firebase";
import { redactSensitiveRoutePathname } from "./routePath";

let pendingInpEvent = null;
let analyticsReadinessPromise = null;
let analyticsRetryTimer = null;
let analyticsRetryAttempt = 0;
const sentEventFingerprints = new Set();
const ANALYTICS_RETRY_DELAYS_MS = [1000, 3000, 10000];
const INTERACTION_TARGET_HISTORY_LIMIT = 100;
// Event Timing startTime can be rounded to a millisecond. Never associate
// an INP entry with another nearby interaction just to fill missing attribution.
const INTERACTION_TARGET_MAX_DELTA_MS = 1;
const capturedInteractionTargets = [];
const initialPagePath =
  typeof window === "undefined"
    ? "/"
    : redactSensitiveRoutePathname(window.location.pathname || "/");

const bounded = (value, maximum = 100) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maximum);

const optionalRounded = (value) =>
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? null
    : Math.round(Number(value));

const interactionTimestamp = (timestamp) => {
  if (timestamp === null || timestamp === undefined) return null;
  const value = Number(timestamp);
  if (!Number.isFinite(value)) return null;
  if (
    value > 1000000000000 &&
    typeof performance !== "undefined" &&
    Number.isFinite(performance.timeOrigin)
  ) {
    return value - performance.timeOrigin;
  }
  return value;
};

const interactionTargetLabel = (target) => {
  let element = target;
  while (element && element.nodeType !== 1) element = element.parentElement;
  if (target?.nodeType === 9) return "document";
  if (!element || !element.tagName) return "";
  const tagName = String(element.tagName).toLowerCase();
  const role = String(element.getAttribute?.("role") || "")
    .toLowerCase()
    .replace(/[^a-z-]/g, "")
    .slice(0, 40);
  return role ? `${tagName}[role=${role}]` : tagName;
};

const captureInteractionTarget = (event) => {
  const timestamp = interactionTimestamp(event.timeStamp);
  const target = interactionTargetLabel(event.target);
  if (timestamp === null || !target) return;
  capturedInteractionTargets.push({ timestamp, target, type: event.type });
  if (capturedInteractionTargets.length > INTERACTION_TARGET_HISTORY_LIMIT) {
    capturedInteractionTargets.splice(
      0,
      capturedInteractionTargets.length - INTERACTION_TARGET_HISTORY_LIMIT,
    );
  }
};

const capturedTargetForMetric = (metric) => {
  if (bounded(metric?.attribution?.interactionTarget, 100)) return "";
  // All entries belong to the INP interaction. The slowest event may have no
  // retained target even when another event in the same interaction does.
  for (const entry of metric?.entries || []) {
    const interactionTime = interactionTimestamp(entry.startTime);
    if (interactionTime === null) continue;
    let match = null;
    for (const candidate of capturedInteractionTargets) {
      if (candidate.type !== entry.name) continue;
      const delta = Math.abs(candidate.timestamp - interactionTime);
      if (
        delta <= INTERACTION_TARGET_MAX_DELTA_MS &&
        (!match || delta < match.delta)
      ) {
        match = { ...candidate, delta };
      }
    }
    if (match) return match.target;
  }
  return "";
};

if (typeof window !== "undefined") {
  for (const type of ["pointerdown", "pointerup", "click", "keydown", "keyup"]) {
    window.addEventListener(type, captureInteractionTarget, {
      capture: true,
      passive: true,
    });
  }
}

export const buildWebVitalAnalyticsEvent = (
  metric,
  pathname = "",
  capturedTarget = "",
) => {
  if (!metric || metric.name !== "INP") return null;
  const attribution = metric.attribution || {};
  const inputDelay = optionalRounded(attribution.inputDelay);
  const processingDuration = optionalRounded(attribution.processingDuration);
  const presentationDelay = optionalRounded(attribution.presentationDelay);

  return {
    metric_name: "INP",
    metric_value: Math.round(Number(metric.value || 0)),
    metric_rating: bounded(metric.rating, 24),
    metric_id: bounded(metric.id, 80),
    page_path: bounded(pathname || "/", 100),
    interaction_target:
      bounded(attribution.interactionTarget, 100) || bounded(capturedTarget, 100),
    interaction_type: bounded(attribution.interactionType, 40),
    load_state: bounded(attribution.loadState, 40),
    ...(inputDelay === null ? {} : { input_delay: inputDelay }),
    ...(processingDuration === null
      ? {}
      : { processing_duration: processingDuration }),
    ...(presentationDelay === null
      ? {}
      : { presentation_delay: presentationDelay }),
  };
};

const eventFingerprint = (event) =>
  JSON.stringify([
    event.metric_id,
    event.metric_value,
    event.page_path,
    event.interaction_target,
    event.interaction_type,
  ]);

const sendWebVitalEvent = (analyticsInstance, event) => {
  if (!analyticsInstance || !event) return false;
  const fingerprint = eventFingerprint(event);
  if (sentEventFingerprints.has(fingerprint)) return true;
  try {
    logEvent(analyticsInstance, "web_vital", event);
    sentEventFingerprints.add(fingerprint);
    return true;
  } catch (_error) {
    return false;
  }
};

const schedulePendingEventFlush = () => {
  if (analyticsReadinessPromise || analyticsRetryTimer) return;
  analyticsReadinessPromise = Promise.resolve(getAnalyticsWhenReady())
    .then((analyticsInstance) => {
      if (analyticsInstance && pendingInpEvent) {
        const event = pendingInpEvent;
        if (sendWebVitalEvent(analyticsInstance, event)) {
          pendingInpEvent = null;
          analyticsRetryAttempt = 0;
        }
      }
    })
    .catch(() => {
      // Field performance reporting must remain best-effort.
    })
    .finally(() => {
      analyticsReadinessPromise = null;
      if (
        pendingInpEvent &&
        analyticsRetryAttempt < ANALYTICS_RETRY_DELAYS_MS.length
      ) {
        const delay = ANALYTICS_RETRY_DELAYS_MS[analyticsRetryAttempt];
        analyticsRetryAttempt += 1;
        analyticsRetryTimer = setTimeout(() => {
          analyticsRetryTimer = null;
          schedulePendingEventFlush();
        }, delay);
      }
    });
};

export const recordWebVitalMetric = (metric) => {
  const reportPath =
    typeof window === "undefined"
      ? "/"
      : redactSensitiveRoutePathname(window.location.pathname || "/");
  const event = buildWebVitalAnalyticsEvent(
    metric,
    initialPagePath,
    capturedTargetForMetric(metric),
  );
  if (!event) return;
  event.report_path = bounded(reportPath, 100);

  if (sendWebVitalEvent(analytics, event)) {
    return;
  }

  // onINP can report again as the page lifecycle advances. Keep only the
  // latest value until delayed analytics initialization is ready.
  pendingInpEvent = event;
  schedulePendingEventFlush();
};

export const resetWebVitalReporterForTests = () => {
  if (analyticsRetryTimer) clearTimeout(analyticsRetryTimer);
  pendingInpEvent = null;
  analyticsReadinessPromise = null;
  analyticsRetryTimer = null;
  analyticsRetryAttempt = 0;
  capturedInteractionTargets.splice(0);
  sentEventFingerprints.clear();
};
